/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/* eslint-disable no-restricted-globals */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import jwtDecode from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { clearRedirectPath, formatUserProfile, getRedirectPath, setRedirectPath, } from "../utils";
import { loginViaCoachServerWithToken } from "../utils/coachServer";
import { createLogOutUrl, createLoginUrl } from "../utils/oneLogin";
import { useAuthContext } from "./useAuthContext";
function navigateOutsideOfApp(url) {
    var _a;
    (_a = window.location) === null || _a === void 0 ? void 0 : _a.replace(url);
}
function generateRedirectUrl(redirectPath) {
    var _a, _b;
    return `${(_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.origin) !== null && _b !== void 0 ? _b : ""}${redirectPath}`;
}
/**
 * Hook for managing OneLogin authentication flow
 *
 * @copyright 2021 Noom, Inc.
 * @author nikola
 */
export function useOneLoginFlow() {
    var _a;
    const navigate = useNavigate();
    const { state, hash } = useLocation();
    const locationState = state;
    const { useCSAuth, clientId, state: authState, clearAuthState, csClientId, csServerUrl, csExtras, error: authError, setError, inProgress, setInProgress, setAuthState, onLoginError, onLoginSuccess, reset, shouldReLogin: shouldReLoginFromContext, generateStateAndNonce, } = (_a = useAuthContext()) !== null && _a !== void 0 ? _a : {};
    const shouldReLogin = () => {
        if (!shouldReLoginFromContext) {
            throw new Error("Missing context!");
        }
        return shouldReLoginFromContext();
    };
    /** Start login flow */
    const login = (redirectPath = "/login/callback") => {
        var _a, _b;
        const redirectUrl = generateRedirectUrl(redirectPath);
        if (!generateStateAndNonce) {
            throw new Error("Missing context!");
        }
        const { state: newState, nonce: newNonce } = generateStateAndNonce();
        if (locationState === null || locationState === void 0 ? void 0 : locationState.from) {
            setRedirectPath(locationState.from || redirectPath);
        }
        if (clientId) {
            setError === null || setError === void 0 ? void 0 : setError();
            const authUrl = createLoginUrl(clientId, redirectUrl, (_a = authState === null || authState === void 0 ? void 0 : authState.state) !== null && _a !== void 0 ? _a : newState, (_b = authState === null || authState === void 0 ? void 0 : authState.nonce) !== null && _b !== void 0 ? _b : newNonce);
            navigateOutsideOfApp(authUrl);
        }
        else {
            throw new Error("OneLogin client ID is missing!");
        }
    };
    /** Finish login flow */
    const loginCallback = (successRedirectPath = "/", errorRedirectPath = "/login") => __awaiter(this, void 0, void 0, function* () {
        var _b, _c, _d, _e, _f, _g;
        const response = new URLSearchParams(hash === null || hash === void 0 ? void 0 : hash.replace("#", ""));
        const error = (_b = response.get("error")) !== null && _b !== void 0 ? _b : "";
        const errorMessage = (_c = response.get("error_description")) !== null && _c !== void 0 ? _c : "";
        const onSuccess = (newAuthState) => {
            var _a;
            setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(false);
            const redirectPath = (_a = getRedirectPath()) !== null && _a !== void 0 ? _a : successRedirectPath;
            clearRedirectPath();
            setAuthState === null || setAuthState === void 0 ? void 0 : setAuthState(newAuthState);
            onLoginSuccess === null || onLoginSuccess === void 0 ? void 0 : onLoginSuccess(formatUserProfile(newAuthState));
            navigate(redirectPath, { replace: true });
        };
        const onError = (newError) => {
            setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(false);
            setError === null || setError === void 0 ? void 0 : setError(newError);
            onLoginError === null || onLoginError === void 0 ? void 0 : onLoginError(newError, authState ? formatUserProfile(authState) : undefined);
            navigate(errorRedirectPath, { replace: true });
        };
        if (error) {
            return onError({ type: `onelogin_${error}`, message: errorMessage });
        }
        setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(true);
        const jwtData = jwtDecode((_d = response.get("id_token")) !== null && _d !== void 0 ? _d : "");
        const loginData = {
            name: jwtData.name,
            email: jwtData.email,
            expiresIn: Number(response.get("expires_in")),
            accessToken: (_e = response.get("access_token")) !== null && _e !== void 0 ? _e : "",
            idToken: (_f = response.get("id_token")) !== null && _f !== void 0 ? _f : "",
            nonce: jwtData.nonce,
            state: (_g = response.get("state")) !== null && _g !== void 0 ? _g : "",
            useOneLoginAuth: true,
        };
        let coachServer;
        let csData;
        if (useCSAuth) {
            try {
                if (!csServerUrl ||
                    !csClientId ||
                    !loginData.email ||
                    !loginData.accessToken) {
                    throw new Error("Malformed login response");
                }
                coachServer = yield loginViaCoachServerWithToken({
                    csServerUrl,
                    csClientId,
                    csExtras,
                    email: loginData.email,
                    accessToken: loginData.accessToken,
                });
                csData = { useCSAuth: true, coachServer };
            }
            catch (e) {
                return onError({
                    type: `coachserver_error`,
                    message: e instanceof Error ? e.message : "Unknown Error",
                });
            }
        }
        else {
            csData = { useCSAuth: false };
        }
        const newState = Object.assign(Object.assign(Object.assign({}, loginData), csData), { timestamp: Date.now() });
        return onSuccess(newState);
    });
    /** Start logout flow */
    const logout = (redirectPath = "/") => {
        clearAuthState === null || clearAuthState === void 0 ? void 0 : clearAuthState();
        if (authState === null || authState === void 0 ? void 0 : authState.idToken) {
            const redirectUrl = generateRedirectUrl(redirectPath);
            const authUrl = createLogOutUrl(redirectUrl, authState.idToken, authState.state);
            navigateOutsideOfApp(authUrl);
        }
        else {
            navigate(redirectPath);
        }
    };
    const resetState = () => {
        reset === null || reset === void 0 ? void 0 : reset();
    };
    /** Restart the login flow */
    const reLogin = (loginPath = "/login") => {
        resetState();
        setRedirectPath(location);
        navigate(loginPath);
    };
    return {
        login,
        logout,
        loginCallback,
        reLogin,
        reset: resetState,
        shouldReLogin,
        error: authError,
        inProgress,
        state: authState,
    };
}
//# sourceMappingURL=useOneLoginFlow.js.map